import { router } from "@inertiajs/react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import { cn } from "@/lib/utils"
import { adminAnalyticsPath } from "@/routes"
import type { AdminAnalyticsGrowth } from "@/types/admin-analytics"

/**
 * OKS-66 — Platform growth chart for the Platform Analytics page.
 *
 * Structural copy of OKS-63 `AnalyticsTrendChart` with 3 series
 * (brands / agencies / creators) instead of 2 (completions / approvals).
 * Same a11y posture:
 *   - chart canvas `role="img"` + summary `aria-label` (locale-formatted)
 *   - sibling `<table class="sr-only">` mirror — one row per UTC day,
 *     columns: date / brands / agencies / creators
 *
 * Empty-state behaviour (UX Spec §3 edge): when every datapoint is
 * flat-zero, the body swaps to the per-card empty variant
 * ("No new signups in this window") with an inline "View all-time"
 * `Button` that issues `router.get(adminAnalyticsPath({window:"all"}))`.
 * The card surface itself does NOT collapse (layout stability).
 *
 * Color tokens: `--chart-1` (brands), `--chart-2` (agencies),
 * `--chart-3` (creators). No hardcoded hex.
 */
export interface AnalyticsGrowthChartProps {
  growth: AdminAnalyticsGrowth
  className?: string
}

export function AnalyticsGrowthChart({
  growth,
  className,
}: AnalyticsGrowthChartProps) {
  const { t } = useTranslation()

  const config: ChartConfig = useMemo(
    () => ({
      brands: {
        label: t("admin.analytics.growth.series.brands"),
        theme: { light: "var(--chart-1)", dark: "var(--chart-1)" },
      },
      agencies: {
        label: t("admin.analytics.growth.series.agencies"),
        theme: { light: "var(--chart-2)", dark: "var(--chart-2)" },
      },
      creators: {
        label: t("admin.analytics.growth.series.creators"),
        theme: { light: "var(--chart-3)", dark: "var(--chart-3)" },
      },
    }),
    [t],
  )

  const isEmpty =
    growth.series.length === 0 ||
    growth.series.every(
      (row) => row.brands === 0 && row.agencies === 0 && row.creators === 0,
    )

  return (
    <Card
      data-testid="admin-analytics-growth-chart"
      className={cn("flex flex-col gap-3 p-4 sm:p-6", className)}
    >
      <CardHeader className="p-0">
        <CardTitle className="text-foreground text-base font-semibold">
          {t("admin.analytics.growth.title")}
        </CardTitle>
      </CardHeader>

      <CardContent className="p-0">
        {isEmpty ? (
          <EmptyInWindow />
        ) : (
          <ChartBody config={config} growth={growth} />
        )}
        <SrOnlyMirror growth={growth} />
      </CardContent>
    </Card>
  )
}

interface ChartBodyProps {
  config: ChartConfig
  growth: AdminAnalyticsGrowth
}

function ChartBody({ config, growth }: ChartBodyProps) {
  const { t } = useTranslation()
  const totalBrands = growth.series.reduce((acc, r) => acc + r.brands, 0)
  const totalAgencies = growth.series.reduce((acc, r) => acc + r.agencies, 0)
  const totalCreators = growth.series.reduce((acc, r) => acc + r.creators, 0)

  const ariaSummary = t("admin.analytics.growth.aria_summary", {
    start: growth.range.start,
    end_date: growth.range.end,
    brands: totalBrands,
    agencies: totalAgencies,
    creators: totalCreators,
  })

  return (
    <div
      data-testid="admin-analytics-growth-chart-canvas"
      role="img"
      aria-label={ariaSummary}
    >
      <ChartContainer
        config={config}
        className="aspect-auto h-[200px] w-full sm:h-[240px] lg:h-[260px]"
      >
        <LineChart
          data={growth.series}
          margin={{ left: 4, right: 12, top: 8, bottom: 0 }}
        >
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            minTickGap={24}
            tickFormatter={(value: string) =>
              new Date(value).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
              })
            }
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            allowDecimals={false}
            domain={[0, "auto"]}
            width={32}
          />
          <ChartTooltip content={<ChartTooltipContent />} />
          <ChartLegend
            data-testid="admin-analytics-growth-chart-legend"
            content={<ChartLegendContent />}
          />
          {(["brands", "agencies", "creators"] as const).map((key) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={`var(--color-${key})`}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ChartContainer>
    </div>
  )
}

function EmptyInWindow() {
  const { t } = useTranslation()
  return (
    <div
      data-testid="admin-analytics-growth-chart-empty"
      className="text-muted-foreground flex h-[200px] flex-col items-center justify-center gap-3 text-sm sm:h-[240px] lg:h-[260px]"
    >
      <p className="text-foreground text-sm font-medium">
        {t("admin.analytics.growth.empty_in_window_title")}
      </p>
      <p className="max-w-md text-center">
        {t("admin.analytics.growth.empty_in_window_body")}
      </p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => {
          router.get(
            adminAnalyticsPath({ window: "all" }),
            {},
            {
              only: ["kpis", "growth"],
              preserveScroll: true,
              preserveState: false,
            },
          )
        }}
        data-testid="admin-analytics-growth-chart-view-all-time"
      >
        {t("admin.analytics.growth.empty_in_window_cta")}
      </Button>
    </div>
  )
}

/**
 * Sr-only mirror — one row per day. Screen readers consume this
 * directly; visual users see the chart. Also serves as a stable
 * Playwright selector (canvas testing is fragile).
 */
function SrOnlyMirror({ growth }: { growth: AdminAnalyticsGrowth }) {
  const { t } = useTranslation()
  return (
    <table
      className="sr-only"
      data-testid="admin-analytics-growth-chart-mirror-table"
    >
      <caption>{t("admin.analytics.growth.mirror_table_caption")}</caption>
      <thead>
        <tr>
          <th>{t("admin.analytics.growth.mirror_table.date")}</th>
          <th>{t("admin.analytics.growth.mirror_table.brands")}</th>
          <th>{t("admin.analytics.growth.mirror_table.agencies")}</th>
          <th>{t("admin.analytics.growth.mirror_table.creators")}</th>
        </tr>
      </thead>
      <tbody>
        {growth.series.map((row) => (
          <tr key={row.date}>
            <td>{row.date}</td>
            <td>{row.brands}</td>
            <td>{row.agencies}</td>
            <td>{row.creators}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
